import React from 'react';
import { CalendarDays, TrendingUp, Percent, DollarSign, Scale, Briefcase, FileSearch, AlertCircle } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета стоимости арбитража
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  // Расчет процента госпошлины от суммы иска (для имущественных требований)
  const courtFeePercent = results.claimType === 'property' ? (results.originalCourtFee / results.claimAmount) * 100 : 0;

  // Определение названий типов суда, иска и т.д.
  const courtTypeNames = {
    'arbitration': 'Арбитражный суд',
    'civil': 'Суд общей юрисдикции',
    'administrative': 'Административный суд',
    'commercial': 'Коммерческий арбитраж'
  };

  const claimTypeNames = {
    'property': 'Имущественный спор',
    'non_property': 'Неимущественный спор',
    'contract': 'Договорной спор',
    'administrative': 'Административный спор',
    'bankruptcy': 'Дело о банкротстве'
  };

  const disputeCategoryNames = {
    'general': 'Общий порядок',
    'simplified': 'Упрощенный порядок',
    'special': 'Особое производство'
  };

  const plaintiffStatusNames = {
    'company': 'Юридическое лицо',
    'individual': 'Физическое лицо',
    'entrepreneur': 'Индивидуальный предприниматель'
  };

  const benefitTypeNames = {
    'none': 'Без льгот',
    'social': 'Социально уязвимые категории',
    'disabled': 'Инвалиды',
    'pensioner': 'Пенсионеры',
    'veteran': 'Ветераны'
  };

  return (
    <ToolResult title="Результаты расчета судебных расходов">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Госпошлина */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <Scale className="w-4 h-4"/>
            Государственная пошлина
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {formatCurrency(results.discountedCourtFee)}
            </span>
            {results.originalCourtFee !== results.discountedCourtFee && (
              <span className="text-sm line-through text-indigo-400 dark:text-indigo-500">
                {formatCurrency(results.originalCourtFee)}
              </span>
            )}
          </div>
          {results.claimType === 'property' && (
            <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
              {formatNumber(courtFeePercent, 2)}% от суммы иска
            </p>
          )}
        </div>

        {/* Общая стоимость */}
        <div className="rounded-lg bg-gradient-to-br from-rose-50 to-orange-50 dark:from-rose-900/30 dark:to-orange-900/30 p-5 border border-rose-100 dark:border-rose-800/50 shadow-sm">
          <h3 className="text-base font-medium text-rose-700 dark:text-rose-300 mb-1.5 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4"/>
            Общая стоимость дела
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-rose-800 dark:text-rose-200" aria-live="polite">
              {formatCurrency(results.totalCost)}
            </span>
            <span className="text-sm font-medium text-rose-600 dark:text-rose-400">
              (включая все расходы)
            </span>
          </div>
          <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">
            Госпошлина + юридические услуги + экспертизы + иные расходы
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Сумма иска" 
          value={formatCurrency(results.claimAmount)} 
          color="indigo" 
          icon={<DollarSign className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Тип суда" 
          value={courtTypeNames[results.courtType] || results.courtType} 
          color="blue" 
          icon={<Scale className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Тип иска" 
          value={claimTypeNames[results.claimType] || results.claimType} 
        />
        
        <ResultItem 
          label="Категория спора" 
          value={disputeCategoryNames[results.disputeCategory] || results.disputeCategory}  
        />
        
        <ResultItem 
          label="Статус истца" 
          value={plaintiffStatusNames[results.plaintiffStatus] || results.plaintiffStatus} 
        />
        
        <ResultItem 
          label="Количество ответчиков" 
          value={results.defendantCount} 
        />
        
        {results.benefitType !== 'none' && results.discountRate < 1 && (
          <ResultItem 
            label="Примененная льгота" 
            value={`${benefitTypeNames[results.benefitType]} (${formatNumber((1 - results.discountRate) * 100, 0)}% скидка)`} 
            color="green" 
          />
        )}
        
        {results.installmentMonths > 0 && (
          <ResultItem 
            label="Рассрочка госпошлины" 
            value={`Первоначальный взнос ${formatCurrency(results.initialPayment)}, затем ${formatCurrency(results.monthlyPayment)} / мес. × ${results.installmentMonths} мес.`} 
            color="purple" 
            icon={<CalendarDays className="w-4 h-4"/>} 
          />
        )}
        
        <div className="border-t border-gray-200 dark:border-gray-700 my-4 pt-4">
          <ResultItem 
            label="Госпошлина (без льгот)" 
            value={formatCurrency(results.originalCourtFee)} 
          />
          
          {results.discountedCourtFee !== results.originalCourtFee && (
            <ResultItem 
              label="Госпошлина (с учетом льгот)" 
              value={formatCurrency(results.discountedCourtFee)} 
              color="green" 
              icon={<Percent className="w-4 h-4"/>} 
            />
          )}
          
          {results.additionalFees !== 0 && (
            <ResultItem 
              label="Дополнительные сборы" 
              value={formatCurrency(results.additionalFees)} 
              color={results.additionalFees < 0 ? 'green' : 'orange'} 
            />
          )}
          
          <ResultItem 
            label="ИТОГО госпошлина" 
            value={formatCurrency(results.totalCourtFee)} 
            color="indigo" 
            isHighlighted={true}
          />
        </div>
        
        <div className="border-t border-gray-200 dark:border-gray-700 my-4 pt-4">
          {results.lawyerFees > 0 && (
            <ResultItem 
              label="Услуги юриста" 
              value={formatCurrency(results.lawyerFees)} 
              color="blue" 
              icon={<Briefcase className="w-4 h-4"/>} 
            />
          )}
          
          {results.expertFees > 0 && (
            <ResultItem 
              label="Судебная экспертиза" 
              value={formatCurrency(results.expertFees)} 
              color="amber" 
              icon={<FileSearch className="w-4 h-4"/>} 
            />
          )}
          
          {results.otherExpenses > 0 && (
            <ResultItem 
              label="Иные расходы" 
              value={formatCurrency(results.otherExpenses)} 
              color="orange" 
            />
          )}
          
          <ResultItem 
            label="ИТОГО дополнительные расходы" 
            value={formatCurrency(results.totalLegalFees)} 
            color="rose" 
            isHighlighted={true}
          />
        </div>
        
        <ResultItem 
          label="ВСЕГО расходов по делу" 
          value={formatCurrency(results.totalCost)} 
          color="purple" 
          isHighlighted={true}
          isBold={true}
          isLarge={true}
        />

        {/* Информация о возмещении судебных расходов */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <AlertCircle className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Возмещение судебных расходов</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                В случае выигрыша дела вы можете взыскать с проигравшей стороны судебные расходы, включая госпошлину и иные обоснованные затраты. 
                Суд может уменьшить взыскиваемую сумму, если посчитает расходы чрезмерными.
              </p>
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;